import { Avatar, Popover } from '@cinesend/ui-kit'
import { Airplane, CaretDown, CaretRight, Check, Gear, SignOut, User, UserPlus } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import preferredCycle from '../../helpers/preferred_cycle'

const nameInitials = (value) => {
  const letters = String(value ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('')
  return letters || '?'
}

const LogoMark = ({ name, imageSrc, size = 'md' }) => {
  const src = typeof imageSrc === 'string' ? imageSrc : ''
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setFailed(false)
  }, [src])

  const showImage = Boolean(src) && !failed

  return (
    <span
      className={showImage ? 'cs-avatar cs-org-logo' : 'cs-avatar cs-org-logo cs-org-logo--letter'}
      data-size={size}
      aria-hidden='true'
    >
      {showImage ? (
        <img className='cs-avatar__image' src={src} alt='' onError={() => setFailed(true)} />
      ) : (
        <span className='cs-avatar__initials'>{nameInitials(name)}</span>
      )}
    </span>
  )
}

const AccountMenu = ({ user, logOut }) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { airlineID } = useParams()
  const [open, setOpen] = useState(false)
  const { data: airlines } = useQuery(['airlines'])
  const { data: airlineFromId } = useQuery(['airlines/', airlineID], { enabled: Boolean(airlineID) })
  const airlineFromList = airlines?.find((item) => Number(item.id) === Number(airlineID))
  const airline = airlineFromId ?? airlineFromList
  const choose = (action) => {
    setOpen(false)
    action()
  }

  const openSettings = (to) => {
    if (!pathname.startsWith('/settings')) {
      localStorage.setItem('backToMetadata', pathname)
    }
    choose(() => navigate(to))
  }

  const switchAirline = (id) => {
    const airline = airlines?.find((item) => item.id === id)
    const cycle = preferredCycle(airline?.cycles)
    if (!cycle) return
    choose(() => navigate(`/airlines/${id}/cycles/${cycle.id}/dashboard`))
  }

  return (
    <Popover
      align='start'
      open={open}
      onOpenChange={setOpen}
      className='cs-account-menu'
      aria-label='Account'
      trigger={
        <button type='button' className='cs-workspace-switcher cs-workspace-switcher--default'>
          <span className='cs-workspace-switcher__leading'>
            <LogoMark name={airline?.name} imageSrc={airline?.logo_url} size='xl' />
          </span>
          <span className='cs-workspace-switcher__label'>{airline?.name}</span>
          <span className='cs-workspace-switcher__trailing' aria-hidden='true'>
            <span className='cs-menu-caret'>
              <CaretDown size={20} weight='regular' />
            </span>
          </span>
        </button>
      }
    >
      <div className='cs-organization-menu__summary'>
        <div className='cs-organization-menu__identity'>
          <LogoMark name={airline?.name} imageSrc={airline?.logo_url} />
          <div className='cs-organization-menu__identity-copy'>
            <span className='cs-organization-menu__title'>{airline?.name}</span>
            <span className='cs-organization-menu__subtitle'>Viasat</span>
          </div>
        </div>
      </div>
      <div role='menu'>
        <SwitchAirline airlines={airlines ?? []} airlineID={airlineID} onSelect={switchAirline} />
      </div>
      <div className='cs-dropdown-menu__divider' role='separator' />
      <div className='cs-organization-menu__summary'>
        <div className='cs-organization-menu__identity'>
          <Avatar
            name={user?.organization?.name || '?'}
            imageSrc={user?.organization?.logo_url || undefined}
            size='md'
            className={user?.organization?.logo_url ? 'cs-org-logo' : 'cs-org-logo cs-org-logo--letter'}
          />
          <div className='cs-organization-menu__identity-copy'>
            <span className='cs-organization-menu__title'>{user?.organization?.name}</span>
          </div>
        </div>
      </div>
      <div role='menu'>
        <MenuItem
          icon={<UserPlus size={20} weight='regular' />}
          label='Invite members'
          onClick={() => openSettings('/settings/account/members')}
        />
        <MenuItem
          icon={<Gear size={20} weight='regular' />}
          label='Organization settings'
          onClick={() => openSettings('/settings/account/organization')}
        />
      </div>
      <div className='cs-dropdown-menu__divider' role='separator' />
      <div className='cs-organization-menu__profile'>
        <LogoMark name={user?.name} imageSrc={user?.avatar_url} />
        <div>
          <span>{user?.name}</span>
          <span>{user?.email}</span>
        </div>
      </div>
      <div role='menu'>
        <MenuItem
          icon={<User size={20} weight='regular' />}
          label='Account settings'
          onClick={() => {
            if (!pathname.startsWith('/settings')) {
              localStorage.setItem('backToMetadata', pathname)
            }
            choose(() => navigate('/settings/account'))
          }}
        />
        <MenuItem
          icon={<SignOut size={20} weight='regular' />}
          label='Sign out'
          onClick={() => choose(() => logOut.mutate())}
        />
      </div>
    </Popover>
  )
}

const MenuItem = ({ icon, label, onClick }) => (
  <button type='button' role='menuitem' className='cs-dropdown-menu__item' onClick={onClick}>
    <span className='cs-dropdown-menu__icon' aria-hidden='true'>
      {icon}
    </span>
    <span className='cs-dropdown-menu__label'>{label}</span>
  </button>
)

const SwitchAirline = ({ airlines, airlineID, onSelect }) => {
  const [open, setOpen] = useState(false)
  const closeTimer = useRef(null)
  const currentID = Number(airlineID)

  const show = () => {
    window.clearTimeout(closeTimer.current)
    setOpen(true)
  }

  const hide = () => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 100)
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      side='right'
      align='start'
      className='cs-account-menu'
      aria-label='Switch airline'
      trigger={
        <button
          type='button'
          role='menuitem'
          className='cs-dropdown-menu__item'
          aria-haspopup='menu'
          aria-expanded={open}
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          <span className='cs-dropdown-menu__icon' aria-hidden='true'>
            <Airplane size={20} weight='regular' />
          </span>
          <span className='cs-dropdown-menu__label'>Switch airline</span>
          <span className='cs-dropdown-menu__trailing' aria-hidden='true'>
            <CaretRight size={20} weight='regular' />
          </span>
        </button>
      }
    >
      <div role='menu' onMouseEnter={show} onMouseLeave={hide}>
        {airlines.map((airline) => {
          const selected = Number(airline.id) === currentID
          return (
            <button
              key={airline.id}
              type='button'
              role='menuitem'
              className='cs-dropdown-menu__item'
              onClick={() => onSelect(airline.id)}
            >
              <span className='cs-dropdown-menu__label'>{airline.name}</span>
              {selected ? (
                <span className='cs-dropdown-menu__trailing' aria-hidden='true'>
                  <Check size={20} weight='regular' />
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
    </Popover>
  )
}

export default AccountMenu
